'use client'

import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { FetchError } from 'ofetch'
import { clientRequest } from 'gpApi/typed-request'
import { useUser } from '@shared/hooks/useUser'
import { useSnackbar } from 'helpers/useSnackbar'
import { trackEvent, EVENTS } from 'helpers/analyticsHelper'
import { extractApiErrorInfo } from 'helpers/extractApiErrorInfo'
import { PRO_COMPLIANCE_SUPPORT_EMAIL } from '@shared/utils/supportContact'
import {
  COMPLIANCE_STATE_QUERY_KEY,
  TCR_COMPLIANCE_QUERY_KEY,
} from 'app/dashboard/profile/texting-compliance/util/tcrCompliance.util'
import type { TcrCompliance } from 'helpers/types'

// gp-api answers 409 when CampaignVerify has not issued a PIN at all — the
// candidate has nothing to get right, so blaming their input sent them into an
// unwinnable retry loop (ENG-10866). 400/422 is a genuine mismatch against an
// issued PIN; anything else is an upstream/Peerly failure. The tax-exempt
// errorCode is also a 422, so it is checked first: the PIN verified, but
// Peerly will never accept this registration until support fixes it
// (ENG-11389).
const messageForError = (e: unknown): string => {
  if (e instanceof FetchError) {
    if (
      extractApiErrorInfo(e.data).errorCode ===
      'CAMPAIGN_VERIFY_TAX_EXEMPT_BRAND'
    ) {
      return (
        'Your registration is listed as a tax-exempt nonprofit, so we ' +
        `can’t finish it. Email ${PRO_COMPLIANCE_SUPPORT_EMAIL} so we can ` +
        'fix it.'
      )
    }
    if (e.status === 409) {
      return (
        'CampaignVerify hasn’t issued your PIN yet, so there’s nothing to ' +
        'enter. We’ll email you as soon as it’s on its way.'
      )
    }
    if (e.status === 400 || e.status === 422) {
      return 'That PIN didn’t match. Double-check and try again.'
    }
  }
  return 'We couldn’t verify that PIN. Please try again.'
}

interface UseSubmitCvPinOptions {
  // Runs after a successful submit (analytics + snackbar + cache invalidation
  // have already fired). EnterPin uses it to redirect; the in-place profile
  // card leaves it unset and lets the invalidated query re-render the surface.
  onSuccess?: () => void
}

interface UseSubmitCvPinResult {
  submit: (pin: string) => Promise<void>
  submitting: boolean
  error: string | null
}

// Shared CampaignVerify PIN submission for both the standalone /enter-pin page
// and the Pro-upgrade in-place compliance card, so the endpoint call, error
// classification, analytics, and cache invalidation can't drift between them.
export function useSubmitCvPin(
  tcrCompliance: TcrCompliance | null | undefined,
  { onSuccess }: UseSubmitCvPinOptions = {},
): UseSubmitCvPinResult {
  const queryClient = useQueryClient()
  const { successSnackbar } = useSnackbar()
  const [user] = useUser()

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (pin: string): Promise<void> => {
    if (!tcrCompliance) return
    setSubmitting(true)
    setError(null)
    try {
      // tcrComplianceId is a path param — typed-request strips it from the
      // body before sending; only `pin` lands in the POST body.
      await clientRequest(
        'POST /v1/campaigns/tcr-compliance/:tcrComplianceId/submit-cv-pin',
        { tcrComplianceId: tcrCompliance.id, pin },
      )

      trackEvent(EVENTS.Outreach.DlcCompliance.PinVerificationCompleted, {
        email: user?.email,
        dlcComplianceStatus: 'Yes',
      })

      successSnackbar('PIN submitted — your application is in review.')
      await queryClient.invalidateQueries({
        queryKey: TCR_COMPLIANCE_QUERY_KEY,
      })
      // Clear the loading state on success too. EnterPin navigates away via
      // onSuccess so it never relied on this, but the in-place card stays
      // mounted: if the refetch hasn't yet flipped the status off `submitted`,
      // leaving `submitting` true would freeze PinForm with no way to retry.
      setSubmitting(false)
      onSuccess?.()
    } catch (e) {
      // A 409 means the gate's cached status is stale — it let the form render
      // for a candidate CampaignVerify has issued nothing to. Without this the
      // form stays up alongside an error saying there is nothing to submit, and
      // a hard refresh is the only way out. Paired with the gate's staleTime 0,
      // invalidating re-fetches immediately and swaps in the in-progress notice.
      if (e instanceof FetchError && e.status === 409) {
        await queryClient.invalidateQueries({
          queryKey: COMPLIANCE_STATE_QUERY_KEY,
        })
      }
      setError(messageForError(e))
      setSubmitting(false)
    }
  }

  return { submit, submitting, error }
}
